
import { Component, Vue } from 'vue-property-decorator';
import { EventBus } from '@/main';
import SuppliersListDisplay from '@/components/po-builder/SuppliersListDisplay.vue';
import BuyingTableDisplay from '@/components/po-builder/BuyingTableDisplay.vue';
import PurchaseOrderDisplay from '@/components/po-builder/PurchaseOrderDisplay.vue';
import PurchaseOrderListDisplay from '@/components/po-builder/PurchaseOrderListDisplay.vue';
import ProductAnalysisDisplay from '@/components/po-builder/ProductAnalysisDisplay.vue';
import RecommendationExplanationDisplay from '@/components/po-builder/RecommendationExplanationDisplay.vue';
import POReviewResultsDisplay from '@/components/po-builder/POReviewResultsDisplay.vue';
import { SupplierList } from '@/collections/suppliers';

/** Represents an action in the chat response (e.g., DISPLAY_SUPPLIERS_LIST) */
interface POAction {
  action: string;
  parameters?: Record<string, unknown>;
}

/** Represents a message in the chat response */
interface POMessage {
  content: string;
}

/** Response structure from the PO Agent chat */
interface ChatResponse {
  actions?: POAction[];
  messages?: POMessage[];
  current_agent?: string;
}

/** Agent data stored in localStorage */
interface StoredAgent {
  id: string;
  name?: string;
}

/** Payload for supplier selection events */
interface SupplierSelectionPayload {
  supplierId: string | number;
  supplierName: string;
  sessionId?: string;
}

/** Payload for products loaded events from BuyingTableDisplay */
interface ProductsLoadedPayload {
  products: Array<Record<string, any>>;
  supplierId: string;
  supplierName: string;
  totalCount: number;
}

@Component({
  name: 'PurchaseAssistant',
  components: {
    SuppliersListDisplay,
    BuyingTableDisplay,
    PurchaseOrderDisplay,
    PurchaseOrderListDisplay,
    ProductAnalysisDisplay,
    RecommendationExplanationDisplay,
    POReviewResultsDisplay,
  },
})
export default class PurchaseAssistant extends Vue {
  // State
  public loading: boolean = false;
  public hasActivePreview: boolean = false;
  public chatResponse: ChatResponse | null = null;
  public currentAgent: string = '';
  public isChatboxOpen: boolean = false;

  // Displayed products synced from BuyingTableDisplay for agent context
  public displayedProducts: Array<Record<string, any>> = [];
  public displayedSupplierId: string = '';
  public displayedSupplierName: string = '';

  // Direct display state (bypass agent for faster response)
  public directDisplayMode: 'buying-table' | 'po-list' | null = null;
  public directDisplayParams: Record<string, any> | null = null;
  public showSupplierDialog: boolean = false;
  public pendingAction: string | null = null;
  public selectedSupplierForAction: { id: number; name: string } | null = null;
  public suppliersList: Array<{ id: number; name: string }> = [];
  public loadingSuppliers: boolean = false;
  public suppliersError: string | null = null;

  /**
   * Start an action from the welcome screen action cards
   * Uses direct display mode to bypass agent for faster response
   */
  public async startAction(action: string) {
    switch (action) {
      case 'buying-table':
        // Show buying table directly (all suppliers if none selected)
        this.showBuyingTableDirect();
        break;

      case 'create-po':
        // Need supplier selection first, then show buying table for that supplier
        this.pendingAction = 'create-po';
        await this.loadSuppliersList();
        this.showSupplierDialog = true;
        break;

      case 'review-pos':
        // Show PO list directly
        this.showPOListDirect();
        break;

      default:
        return;
    }
  }

  /**
   * Check if a specific component should be shown based on action messages
   */
  public shouldShowComponent(componentKey: string): boolean {
    return this.findAction(componentKey) !== null;
  }

  /**
   * Get parameters for a specific action
   */
  public getActionParameters(actionKey: string): Record<string, unknown> {
    return this.findAction(actionKey)?.parameters || {};
  }

  /**
   * Get a unique key for an action to force Vue re-rendering
   * Uses primaryVariantId or requestedSku from parameters
   */
  public getActionKey(actionKey: string): string {
    const rawParams = this.getActionParameters(actionKey);

    // Validate params is an object
    if (typeof rawParams !== 'object' || rawParams === null) {
      return actionKey;
    }
    const params = rawParams as Record<string, unknown>;

    // Use primaryVariantId, requestedSku, or query to create a stable key
    const primaryId = typeof params.primaryVariantId === 'string' ? params.primaryVariantId : '';
    const requestedSku = typeof params.requestedSku === 'string' ? params.requestedSku : '';

    // Guard match_info as object before reading .query
    let query = '';
    const matchInfo = params.match_info;
    if (typeof matchInfo === 'object' && matchInfo !== null) {
      const matchInfoObj = matchInfo as Record<string, unknown>;
      query = typeof matchInfoObj.query === 'string' ? matchInfoObj.query : '';
    }

    // Include variants count to detect changes in multi-variant responses
    let variantKey = '';
    const variants = params.variants;
    if (Array.isArray(variants) && variants.length > 0) {
      const firstVariant = variants[0];
      if (typeof firstVariant === 'object' && firstVariant !== null) {
        const variantObj = firstVariant as Record<string, unknown>;
        const variantId = typeof variantObj.variant_id === 'string' || typeof variantObj.variant_id === 'number'
          ? String(variantObj.variant_id)
          : '';
        variantKey = `${variants.length}-${variantId}`;
      } else {
        variantKey = `${variants.length}-`;
      }
    }

    return `${actionKey}-${primaryId}-${requestedSku}-${query}-${variantKey}`;
  }

  /**
   * Generate a key for BuyingTableDisplay that includes date_range for re-rendering
   * when the date range changes
   */
  public getBuyingTableKey(): string {
    const params = this.getActionParameters('DISPLAY_BUYING_TABLE');
    if (!params || typeof params !== 'object') {
      return 'buying-table';
    }
    const typedParams = params as Record<string, unknown>;
    const supplierId = typedParams.supplier_id || '';
    const location = typedParams.location || '';

    // Include date_range in key to force re-render when it changes
    let dateRangeKey = '';
    const dateRange = typedParams.date_range;
    if (dateRange && typeof dateRange === 'object') {
      const dr = dateRange as Record<string, unknown>;
      dateRangeKey = `${dr.start || ''}-${dr.end || ''}`;
    }

    return `buying-table-${supplierId}-${location}-${dateRangeKey}`;
  }

  /**
   * Handle preview data from agent
   */
  public handleAgentPreview(response: ChatResponse) {
    this.loading = false;
    this.chatResponse = response;
    this.currentAgent = response.current_agent || 'Purchase Assistant';
    this.hasActivePreview = true;

    // Clear direct display mode to show agent-driven components
    // This is needed when transitioning from direct mode (e.g., buying table) to preview mode
    this.directDisplayMode = null;
    this.directDisplayParams = null;

    // Emit completion event
    EventBus.$emit('po-builder:preview-complete');
  }

  /**
   * Reset the preview state
   */
  public reset() {
    this.hasActivePreview = false;
    this.chatResponse = null;
    this.currentAgent = '';
    // Also clear direct display mode
    this.directDisplayMode = null;
    this.directDisplayParams = null;
  }

  /**
   * Chatbox opened handler
   */
  public onChatboxOpened() {
    this.isChatboxOpen = true;
  }

  /**
   * Chatbox closed handler
   */
  public onChatboxClosed() {
    this.isChatboxOpen = false;
  }

  /**
   * Handle supplier selection from the suppliers list
   * Sends message to agent to show buying table for the selected supplier
   */
  public handleSupplierSelection(data: SupplierSelectionPayload) {
    // Send the selection to the chatbox to communicate with the agent
    // Message triggers the agent to display the buying table for this supplier
    // Include both name (user-friendly) and id (for agent DB lookup)
    EventBus.$emit('chatbox:send-message', {
      message: `Show buying table for ${data.supplierName} (id:${data.supplierId})`,
      metadata: {
        action: 'show_buying_table',
        supplierId: data.supplierId,
        supplierName: data.supplierName,
        // Include displayed products for agent context sync
        ...(this.displayedProducts.length > 0 && { displayed_products: this.displayedProducts }),
      },
    });
  }

  /**
   * Handle create PO request from SuppliersListDisplay.
   * Sends a message to the agent to start the PO creation flow for the supplier.
   */
  public handleCreatePOForSupplier(data: SupplierSelectionPayload) {
    // Send message to agent to create PO for this supplier
    // The message format includes supplier ID for agent to extract
    EventBus.$emit('chatbox:send-message', {
      message: `Create purchase order for ${data.supplierName} (id:${data.supplierId})`,
      metadata: {
        action: 'create_po_for_supplier',
        supplierId: data.supplierId,
        supplierName: data.supplierName,
      },
    });
  }

  /**
   * Handle products loaded event from BuyingTableDisplay
   * Stores the displayed products for inclusion in subsequent chat messages
   */
  public handleProductsLoaded(data: ProductsLoadedPayload) {
    this.displayedProducts = data.products;
    this.displayedSupplierId = data.supplierId;
    this.displayedSupplierName = data.supplierName;
  }

  public async mounted() {
    // Register EventBus listener for agent preview events
    EventBus.$on('po-builder:preview', this.handleAgentPreview);

    // Register EventBus listeners for chatbox state changes
    EventBus.$on('chatbox:opened', this.onChatboxOpened);
    EventBus.$on('chatbox:closed', this.onChatboxClosed);

    // Register listener for supplier selection
    EventBus.$on('po-builder:supplier-selected', this.handleSupplierSelection);

    // Register listener for create PO button from SuppliersListDisplay
    EventBus.$on('po-builder:create-po-for-supplier', this.handleCreatePOForSupplier);

    // Register listener for products loaded from BuyingTableDisplay
    EventBus.$on('po-builder:products-loaded', this.handleProductsLoaded);

    // Initialize chatbox with PO agent using request-response pattern
    // This ensures we have the actual chatbox state, not stale localStorage data
    await this.$nextTick();

    const chatboxStatus = await this.getChatboxStatus();
    this.isChatboxOpen = chatboxStatus.isOpen;

    if (chatboxStatus.isOpen) {
      // Chatbox already open - just ensure PO agent is selected
      await this.ensurePOAgentSelected();
    } else {
      // Chatbox not open - open it and select PO agent
      await this.openChatboxWithPOAgent();
    }
  }

  public beforeDestroy() {
    // Clean up event listeners
    EventBus.$off('po-builder:preview', this.handleAgentPreview);
    EventBus.$off('chatbox:opened', this.onChatboxOpened);
    EventBus.$off('chatbox:closed', this.onChatboxClosed);
    EventBus.$off('po-builder:supplier-selected', this.handleSupplierSelection);
    EventBus.$off('po-builder:create-po-for-supplier', this.handleCreatePOForSupplier);
    EventBus.$off('po-builder:products-loaded', this.handleProductsLoaded);
  }

  /**
   * Handle supplier selection confirmation from dialog
   */
  public confirmSupplierSelection() {
    if (!this.selectedSupplierForAction) {
      return;
    }

    const { id, name } = this.selectedSupplierForAction;
    this.showSupplierDialog = false;

    if (this.pendingAction === 'create-po') {
      // Show buying table for selected supplier to create PO
      this.showBuyingTableDirect(id, name);
    }

    // Clear pending state
    this.pendingAction = null;
    this.selectedSupplierForAction = null;
  }

  /**
   * Clear direct display mode and return to welcome screen
   */
  public clearDirectDisplay() {
    this.directDisplayMode = null;
    this.directDisplayParams = null;
    this.hasActivePreview = false;
    this.currentAgent = '';
  }

  // ========== Private Direct Display Methods (Bypass Agent) ==========

  /**
   * Show buying table directly without agent round-trip
   * @param supplierId Optional supplier ID to filter by
   * @param supplierName Optional supplier name for display
   */
  private showBuyingTableDirect(supplierId?: number, supplierName?: string) {
    // Use provided supplier or context supplier if available
    let supplier: { id: number; name: string } | null = null;
    if (supplierId) {
      supplier = { id: supplierId, name: supplierName || 'Unknown' };
    } else if (this.displayedSupplierId) {
      const parsedId = parseInt(String(this.displayedSupplierId), 10);
      if (Number.isFinite(parsedId)) {
        supplier = { id: parsedId, name: this.displayedSupplierName || 'Unknown' };
      }
    }

    this.directDisplayParams = {
      supplier_id: supplier?.id ? String(supplier.id) : '',
      supplier_name: supplier?.name || 'All Suppliers',
      location: 'all',
    };
    this.directDisplayMode = 'buying-table';
    this.hasActivePreview = true;
    this.currentAgent = 'Buying Table';

    // Clear any pending agent preview
    this.chatResponse = null;
  }

  /**
   * Show purchase order list directly without agent round-trip
   */
  private showPOListDirect() {
    // Use context supplier for filtering if available
    let supplier: { id: number; name: string } | null = null;
    if (this.displayedSupplierId) {
      const parsedId = parseInt(String(this.displayedSupplierId), 10);
      if (Number.isFinite(parsedId)) {
        supplier = { id: parsedId, name: this.displayedSupplierName || '' };
      }
    }

    this.directDisplayParams = {
      supplier_id: supplier?.id ? String(supplier.id) : '',
      supplier_name: supplier?.name || '',
    };
    this.directDisplayMode = 'po-list';
    this.hasActivePreview = true;
    this.currentAgent = 'Purchase Orders';

    // Clear any pending agent preview
    this.chatResponse = null;
  }

  /**
   * Load suppliers list for the supplier selection dialog
   */
  private async loadSuppliersList() {
    this.loadingSuppliers = true;
    this.suppliersError = null;
    try {
      const suppliers = new SupplierList();
      await suppliers.fetch();
      this.suppliersList = suppliers.items.map((s) => ({
        id: s.id,
        name: s.name,
      }));
    } catch (error) {
      console.error('Failed to load suppliers:', error);
      this.suppliersError = 'Failed to load suppliers. Please try again.';
      this.suppliersList = [];
    }
    this.loadingSuppliers = false;
  }

  /**
   * Wait for chatbox to be ready after opening
   */
  private waitForChatboxReady(): Promise<void> {
    return new Promise((resolve) => {
      // Set a timeout to prevent hanging indefinitely
      const timeout = setTimeout(() => {
        resolve();
      }, 1000);

      // Listen for chatbox ready event
      const handler = () => {
        clearTimeout(timeout);
        resolve();
      };

      EventBus.$once('chatbox:ready', handler);

      // Also resolve on chatbox:opened as fallback
      EventBus.$once('chatbox:opened', () => {
        // Small delay to ensure chatbox is fully initialized
        setTimeout(() => {
          clearTimeout(timeout);
          resolve();
        }, 200);
      });
    });
  }

  /**
   * Get the actual chatbox status using request-response pattern
   * This avoids race conditions with localStorage-based checks
   */
  private getChatboxStatus(): Promise<{ isOpen: boolean }> {
    return new Promise((resolve) => {
      // Set a timeout to prevent hanging if no response
      const timeout = setTimeout(() => {
        // Fallback: check localStorage if no response received
        const stored = localStorage.getItem('assisty_chatbox_open');
        resolve({ isOpen: stored === 'true' });
      }, 500);

      const handler = (status: { isOpen: boolean }) => {
        clearTimeout(timeout);
        resolve(status);
      };

      EventBus.$once('chatbox:status-response', handler);
      EventBus.$emit('chatbox:get-status');
    });
  }

  /**
   * Open chatbox and select PO agent, waiting for ready state
   */
  private async openChatboxWithPOAgent(): Promise<void> {
    const readyPromise = this.waitForChatboxReady();
    EventBus.$emit('chatbox:open-and-select-agent', 'purchase-order');
    await readyPromise;
    this.isChatboxOpen = true;
  }

  /**
   * Ensure PO agent is selected in an already-open chatbox
   */
  private async ensurePOAgentSelected(): Promise<void> {
    const currentAgent = localStorage.getItem('assisty_last_selected_agent');
    const agent = currentAgent ? this.safeParseJSON(currentAgent) as StoredAgent | null : null;

    if (!agent || (agent.id !== 'purchase-order' && agent.id !== 'po-agent')) {
      EventBus.$emit('chatbox:select-agent', 'purchase-order');
      // Wait for agent selection to complete
      await new Promise((r) => setTimeout(r, 200));
    }
  }

  /**
   * Find an action by key from chatResponse.actions or chatResponse.messages
   * @param actionKey - The action key to search for (e.g., 'DISPLAY_SUPPLIERS_LIST')
   * @returns The action object if found, or null
   */
  private findAction(actionKey: string): POAction | null {
    if (!this.chatResponse) {
      return null;
    }

    // Check actions array first (preferred format)
    if (this.chatResponse.actions && Array.isArray(this.chatResponse.actions)) {
      const action = this.chatResponse.actions.find(
        (a: POAction) => a.action === actionKey,
      );
      if (action) {
        return action;
      }
    }

    // Fallback: Parse from messages array (legacy format)
    if (this.chatResponse.messages && Array.isArray(this.chatResponse.messages)) {
      for (const msg of this.chatResponse.messages) {
        if (typeof msg.content === 'string') {
          try {
            const parsed = JSON.parse(msg.content);
            if (parsed.action === actionKey) {
              return parsed;
            }
          } catch {
            // Legacy string format: check for direct match
            if (msg.content === actionKey || msg.content.includes(actionKey)) {
              return { action: actionKey, parameters: {} };
            }
          }
        }
      }
    }

    return null;
  }

  /**
   * Safely parse JSON string, returning null on failure
   * @param jsonString - The string to parse
   * @returns Parsed object or null if parsing fails
   */
  private safeParseJSON(jsonString: string): unknown {
    try {
      return JSON.parse(jsonString);
    } catch {
      return null;
    }
  }
}
